import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import CaseBillingTab from '../components/CaseBillingTab';
import GenerateDocumentDialog from '../components/GenerateDocumentDialog';
import CaseCustomFieldsEditor from '../components/CaseCustomFieldsEditor';
import CaseCommunicationsTab from '../components/CaseCommunicationsTab';
import CaseEventsTab from '../components/CaseEventsTab';
import NoticeScannerDialog from '../components/NoticeScannerDialog';
import CaseTasksTab from '../components/CaseTasksTab';
import CaseFiledEmails from '../components/CaseFiledEmails';
import SMSComposer from '../components/SMSComposer';
import DocumentUploadDialog from '../components/DocumentUploadDialog';
import DocumentEditDialog from '../components/DocumentEditDialog';
import CaseExaminationTab from '../components/CaseExaminationTab';
import CaseExhibitsTab from '../components/CaseExhibitsTab';
import CaseWorkflowTab from '../components/CaseWorkflowTab';
import {
  Briefcase,
  ChevronLeft,
  ChevronRight,
  Clock,
  DollarSign,
  FileText,
  Users,
  MessageSquare,
  FolderOpen,
  Edit,
  Trash2,
  Upload,
  Download,
  Plus,
  MoreVertical,
  Phone,
  Mail,
  Video,
  Calendar,
  AlertCircle,
  CheckCircle,
  Loader2,
  ArrowRight,
  Folder,
  File,
  X,
  Sparkles,
  Settings,
  Scan,
  Pencil,
  GitBranch,
  Tag,
  ScrollText
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status colors
const statusColors = {
  active: 'bg-emerald-100 text-emerald-700',
  pending: 'bg-amber-100 text-amber-700',
  on_hold: 'bg-orange-100 text-orange-700',
  closed: 'bg-slate-100 text-slate-700',
  archived: 'bg-red-100 text-red-700'
};

// Stage colors
const stageColors = {
  intake: '#3b82f6',
  investigation: '#8b5cf6',
  demand: '#f59e0b',
  negotiation: '#10b981',
  filed: '#ef4444',
  discovery: '#6366f1',
  motions: '#ec4899',
  mediation: '#14b8a6',
  trial_prep: '#f97316',
  trial: '#dc2626',
  post_trial: '#7c3aed',
  appeal: '#0891b2',
  settled: '#22c55e',
  closed: '#64748b'
};

export default function CaseManagementDetailPage() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  // State
  const [caseData, setCaseData] = useState(null);
  const [stages, setStages] = useState([]);
  const [folders, setFolders] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedFolder, setSelectedFolder] = useState(null);

  // Document upload
  const [showUploadDialog, setShowUploadDialog] = useState(false);

  // Document edit
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);

  // Stage change
  const [showStageDialog, setShowStageDialog] = useState(false);
  const [newStage, setNewStage] = useState('');
  const [stageNotes, setStageNotes] = useState('');
  const [changingStage, setChangingStage] = useState(false);

  // Folder creation
  const [showFolderDialog, setShowFolderDialog] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');

  // Generate from template
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);

  // Notice scanner
  const [showNoticeScannerDialog, setShowNoticeScannerDialog] = useState(false);
  const [scanningDocument, setScanningDocument] = useState(null);

  // Fetch data
  useEffect(() => {
    fetchCase();
    fetchStages();
    fetchFolders();
    fetchDocuments();
  }, [caseId, token]);

  const fetchCase = async () => {
    try {
      const res = await fetch(`${API_URL}/api/case-management/cases/${caseId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCaseData(data);
      } else {
        toast.error('Case not found');
        navigate('/case-management');
      }
    } catch (error) {
      console.error('Error fetching case:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStages = async () => {
    try {
      const res = await fetch(`${API_URL}/api/case-management/stages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStages(data.default_stages || []);
      }
    } catch (error) {
      console.error('Error fetching stages:', error);
    }
  };

  const fetchFolders = async () => {
    try {
      const res = await fetch(`${API_URL}/api/case-management/cases/${caseId}/folders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setFolders(data);
      }
    } catch (error) {
      console.error('Error fetching folders:', error);
    }
  };

  const fetchDocuments = async (folderId = null) => {
    try {
      let url = `${API_URL}/api/case-management/cases/${caseId}/documents`;
      if (folderId) url += `?folder_id=${folderId}`;
      
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDocuments(data);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const handleStageChange = async () => {
    if (!newStage) {
      toast.error('Please select a stage');
      return;
    }

    setChangingStage(true);
    try {
      const res = await fetch(`${API_URL}/api/case-management/cases/${caseId}/change-stage?new_stage=${newStage}&notes=${encodeURIComponent(stageNotes)}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Stage updated successfully');
        setShowStageDialog(false);
        setNewStage('');
        setStageNotes('');
        fetchCase();
      } else {
        toast.error('Failed to update stage');
      }
    } catch (error) {
      toast.error('Failed to update stage');
    } finally {
      setChangingStage(false);
    }
  };

  const handleDownload = async (docId, filename) => {
    try {
      const res = await fetch(`${API_URL}/api/case-management/cases/${caseId}/documents/${docId}/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!confirm('Are you sure you want to delete this document?')) return;

    try {
      const res = await fetch(`${API_URL}/api/case-management/cases/${caseId}/documents/${docId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Document deleted');
        fetchDocuments(selectedFolder);
        fetchCase();
      }
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const createFolder = async () => {
    if (!newFolderName) {
      toast.error('Please enter a folder name');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/case-management/cases/${caseId}/folders`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newFolderName,
          parent_id: selectedFolder
        })
      });

      if (res.ok) {
        toast.success('Folder created');
        setShowFolderDialog(false);
        setNewFolderName('');
        fetchFolders();
      }
    } catch (error) {
      toast.error('Failed to create folder');
    }
  };

  const getStageLabel = (stageId) => {
    const stage = stages.find(s => s.id === stageId);
    return stage?.label || stageId;
  };

  const getStageColor = (stageId) => {
    return stageColors[stageId] || '#64748b';
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-navy mx-auto mb-4" />
          <p className="text-muted-foreground">Loading case...</p>
        </div>
      </div>
    );
  }

  if (!caseData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="case-management-detail-page">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/case-management" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
                <ChevronLeft className="w-4 h-4" />
                <span className="text-sm">All Cases</span>
              </Link>
              <div className="h-6 w-px bg-border" />
              <div>
                <h1 className="font-heading text-lg font-bold text-navy">{caseData.name}</h1>
                <p className="text-xs text-muted-foreground">{caseData.case_number}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Badge className={statusColors[caseData.status]}>
                {caseData.status}
              </Badge>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  setNewStage(caseData.current_stage);
                  setShowStageDialog(true);
                }}
              >
                <div 
                  className="w-2 h-2 rounded-full mr-2"
                  style={{ backgroundColor: getStageColor(caseData.current_stage) }}
                />
                {getStageLabel(caseData.current_stage)}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="documents">
              Documents
              <Badge variant="secondary" className="ml-1.5">{caseData.document_count || 0}</Badge>
            </TabsTrigger>
            <TabsTrigger value="exhibits" data-testid="exhibits-tab">
              <Tag className="w-4 h-4 mr-1" />
              Exhibits
            </TabsTrigger>
            <TabsTrigger value="examination" data-testid="examination-tab">
              <ScrollText className="w-4 h-4 mr-1" />
              Examination
            </TabsTrigger>
            <TabsTrigger value="tasks" data-testid="tasks-tab">
              <Briefcase className="w-4 h-4 mr-1" />
              Tasks
            </TabsTrigger>
            <TabsTrigger value="events" data-testid="events-tab">
              <Calendar className="w-4 h-4 mr-1" />
              Events
            </TabsTrigger>
            <TabsTrigger value="billing">Billing & Time</TabsTrigger>
            <TabsTrigger value="communications">Communications</TabsTrigger>
            <TabsTrigger value="workflow" data-testid="workflow-tab">
              <GitBranch className="w-4 h-4 mr-1" />
              Workflow
            </TabsTrigger>
            <TabsTrigger value="custom-fields">
              <Settings className="w-4 h-4 mr-1" />
              Fields
            </TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Main Info */}
              <div className="lg:col-span-2 space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Case Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-muted-foreground">Case Number</Label>
                        <p className="font-medium">{caseData.case_number}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Case Type</Label>
                        <p className="font-medium capitalize">{caseData.case_type?.replace('_', ' ')}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Client</Label>
                        <p className="font-medium">{caseData.client_name}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Opposing Party</Label>
                        <p className="font-medium">{caseData.opposing_party || 'Not specified'}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">Fee Type</Label>
                        <p className="font-medium capitalize">{caseData.fee_type?.replace('_', ' ')}</p>
                      </div>
                      {caseData.hourly_rate && (
                        <div>
                          <Label className="text-muted-foreground">Hourly Rate</Label>
                          <p className="font-medium">${caseData.hourly_rate}/hr</p>
                        </div>
                      )}
                    </div>
                    {caseData.description && (
                      <div>
                        <Label className="text-muted-foreground">Description</Label>
                        <p className="text-sm mt-1">{caseData.description}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Stage Progress */}
                <Card>
                  <CardHeader>
                    <CardTitle>Case Progress</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {(caseData.stages_available || stages.map(s => s.id)).slice(0, 8).map((stageId, idx) => {
                        const isActive = stageId === caseData.current_stage;
                        const isPast = caseData.stage_history?.some(h => h.stage === stageId || h.to_stage === stageId);
                        
                        return (
                          <div 
                            key={stageId}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${
                              isActive 
                                ? 'bg-blue-100 ring-2 ring-blue-500' 
                                : isPast 
                                  ? 'bg-slate-100' 
                                  : 'bg-slate-50 opacity-50'
                            }`}
                          >
                            <div 
                              className={`w-3 h-3 rounded-full ${isActive ? 'animate-pulse' : ''}`}
                              style={{ backgroundColor: getStageColor(stageId) }}
                            />
                            <span className={`text-sm ${isActive ? 'font-semibold' : ''}`}>
                              {getStageLabel(stageId)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Quick Stats */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Quick Stats</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">Documents</span>
                      </div>
                      <Badge variant="secondary">{caseData.document_count || 0}</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">Total Hours</span>
                      </div>
                      <Badge variant="secondary">{caseData.total_hours || 0}</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">Total Billed</span>
                      </div>
                      <span className="font-medium">${(caseData.total_billed || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-500" />
                        <span className="text-sm">Collected</span>
                      </div>
                      <span className="font-medium text-emerald-600">
                        ${(caseData.total_collected || 0).toLocaleString()}
                      </span>
                    </div>
                  </CardContent>
                </Card>

                {/* Quick Actions */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Quick Actions</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <Button 
                      variant="outline" 
                      className="w-full justify-start"
                      onClick={() => {
                        setActiveTab('documents');
                        setShowUploadDialog(true);
                      }}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Document
                    </Button>
                    <Button variant="outline" className="w-full justify-start" disabled>
                      <Clock className="w-4 h-4 mr-2" />
                      Log Time Entry
                    </Button>
                    <Button variant="outline" className="w-full justify-start" disabled>
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Log Communication
                    </Button>
                  </CardContent>
                </Card>

                {/* Intake.AI Link */}
                {caseData.intake_lead_id && (
                  <Card className="bg-emerald-50 border-emerald-200">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="w-4 h-4 text-emerald-600" />
                        <span className="text-sm font-medium text-emerald-800">From Intake.AI</span>
                      </div>
                      <p className="text-xs text-emerald-700">
                        This case was created from a converted lead
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Filed Emails */}
                <CaseFiledEmails caseId={caseId} />

                {/* SMS Messaging */}
                <SMSComposer 
                  caseId={caseId}
                  contactId={caseData?.client_id}
                  contactName={caseData?.client_name}
                  contactPhone={caseData?.client_phone}
                />
              </div>
            </div>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <div className="grid lg:grid-cols-4 gap-6">
              {/* Folder Sidebar */}
              <div className="lg:col-span-1">
                <Card>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Folders</CardTitle>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => setShowFolderDialog(true)}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-1">
                    <button
                      onClick={() => {
                        setSelectedFolder(null);
                        fetchDocuments(null);
                      }}
                      className={`w-full flex items-center gap-2 p-2 rounded-lg text-left transition-colors ${
                        !selectedFolder ? 'bg-slate-100' : 'hover:bg-slate-50'
                      }`}
                    >
                      <FolderOpen className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">All Documents</span>
                    </button>
                    {folders.map((folder) => (
                      <button
                        key={folder.id}
                        onClick={() => {
                          setSelectedFolder(folder.id);
                          fetchDocuments(folder.id);
                        }}
                        className={`w-full flex items-center gap-2 p-2 rounded-lg text-left transition-colors ${
                          selectedFolder === folder.id ? 'bg-slate-100' : 'hover:bg-slate-50'
                        }`}
                      >
                        <Folder className="w-4 h-4 text-amber-500" />
                        <span className="text-sm">{folder.name}</span>
                      </button>
                    ))}
                  </CardContent>
                </Card>
              </div>

              {/* Documents List */}
              <div className="lg:col-span-3">
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">
                        {selectedFolder 
                          ? folders.find(f => f.id === selectedFolder)?.name 
                          : 'All Documents'}
                      </CardTitle>
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="outline" 
                          onClick={() => setShowGenerateDialog(true)}
                          data-testid="generate-from-template-btn"
                        >
                          <Sparkles className="w-4 h-4 mr-2" />
                          Generate from Template
                        </Button>
                        <Button onClick={() => setShowUploadDialog(true)}>
                          <Upload className="w-4 h-4 mr-2" />
                          Upload
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {documents.length > 0 ? (
                      <div className="space-y-2">
                        {documents.map((doc) => (
                          <div 
                            key={doc.id}
                            className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <File className="w-8 h-8 text-blue-500" />
                              <div>
                                <p className="font-medium text-sm">{doc.original_filename}</p>
                                <p className="text-xs text-muted-foreground">
                                  {formatFileSize(doc.file_size)} • {new Date(doc.uploaded_at).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="capitalize">
                                {doc.category}
                              </Badge>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                title="Edit Document"
                                onClick={() => {
                                  setEditingDocument(doc);
                                  setShowEditDialog(true);
                                }}
                                data-testid={`edit-doc-${doc.id}`}
                              >
                                <Pencil className="w-4 h-4 text-slate-600" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                title="Scan for Notice Information"
                                onClick={() => {
                                  setScanningDocument(doc);
                                  setShowNoticeScannerDialog(true);
                                }}
                                data-testid={`scan-notice-${doc.id}`}
                              >
                                <Scan className="w-4 h-4 text-blue-600" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                title="Download"
                                onClick={() => handleDownload(doc.id, doc.original_filename)}
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                title="Delete"
                                onClick={() => handleDeleteDocument(doc.id)}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                        <p className="text-muted-foreground mb-4">No documents in this folder</p>
                        <Button variant="outline" onClick={() => setShowUploadDialog(true)}>
                          <Upload className="w-4 h-4 mr-2" />
                          Upload Document
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Tasks Tab */}
          <TabsContent value="tasks">
            <CaseTasksTab caseId={caseId} caseData={caseData} />
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events">
            <CaseEventsTab caseId={caseId} caseData={caseData} />
          </TabsContent>

          {/* Exhibits Tab */}
          <TabsContent value="exhibits">
            <CaseExhibitsTab caseId={caseId} caseName={caseData?.name || ''} />
          </TabsContent>

          {/* Examination Tab */}
          <TabsContent value="examination">
            <CaseExaminationTab caseId={caseId} caseName={caseData?.name || ''} />
          </TabsContent>

          {/* Workflow Tab */}
          <TabsContent value="workflow">
            <CaseWorkflowTab caseId={caseId} caseName={caseData?.name || ''} />
          </TabsContent>

          {/* Custom Fields Tab */}
          <TabsContent value="custom-fields">
            <CaseCustomFieldsEditor 
              caseId={caseId}
              caseType={caseData?.case_type || ''}
              onUpdate={fetchCase}
            />
          </TabsContent>

          {/* Billing Tab */}
          <TabsContent value="billing">
            <CaseBillingTab 
              caseId={caseId} 
              caseData={caseData} 
              onUpdate={fetchCase}
            />
          </TabsContent>

          {/* Communications Tab */}
          <TabsContent value="communications">
            <CaseCommunicationsTab caseId={caseId} caseData={caseData} />
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle>Case History</CardTitle>
              </CardHeader>
              <CardContent>
                {caseData.stage_history?.length > 0 ? (
                  <div className="space-y-4">
                    {[...caseData.stage_history].reverse().map((entry, idx) => (
                      <div key={idx} className="flex items-start gap-4">
                        <div 
                          className="w-3 h-3 rounded-full mt-1.5"
                          style={{ backgroundColor: getStageColor(entry.to_stage || entry.stage) }}
                        />
                        <div>
                          <p className="font-medium">
                            {entry.from_stage 
                              ? `${getStageLabel(entry.from_stage)} → ${getStageLabel(entry.to_stage)}`
                              : `Started at ${getStageLabel(entry.stage)}`
                            }
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(entry.transitioned_at || entry.entered_at).toLocaleString()}
                          </p>
                          {entry.notes && (
                            <p className="text-sm mt-1">{entry.notes}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground">No history available</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Stage Change Dialog */}
      <Dialog open={showStageDialog} onOpenChange={setShowStageDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Case Stage</DialogTitle>
            <DialogDescription>
              Move this case to a different stage
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>New Stage</Label>
              <Select value={newStage} onValueChange={setNewStage}>
                <SelectTrigger>
                  <SelectValue placeholder="Select stage" />
                </SelectTrigger>
                <SelectContent>
                  {stages.map((stage) => (
                    <SelectItem key={stage.id} value={stage.id}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: stage.color }}
                        />
                        {stage.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                placeholder="Add notes about this stage change..."
                value={stageNotes}
                onChange={(e) => setStageNotes(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStageDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleStageChange} disabled={changingStage}>
              {changingStage ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Update Stage
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog - Enhanced with multi-file and drag & drop */}
      <DocumentUploadDialog
        open={showUploadDialog}
        onOpenChange={setShowUploadDialog}
        caseId={caseId}
        folderId={selectedFolder}
        onUploadComplete={() => {
          fetchDocuments(selectedFolder);
          fetchCase();
        }}
      />

      {/* Create Folder Dialog */}
      <Dialog open={showFolderDialog} onOpenChange={setShowFolderDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Folder</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Folder Name</Label>
              <Input
                placeholder="Enter folder name"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFolderDialog(false)}>
              Cancel
            </Button>
            <Button onClick={createFolder}>
              Create Folder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate from Template Dialog */}
      <GenerateDocumentDialog
        open={showGenerateDialog}
        onOpenChange={setShowGenerateDialog}
        caseId={caseId}
        caseData={caseData}
        folders={folders}
        onGenerated={() => {
          fetchDocuments(selectedFolder);
          fetchCase();
        }}
      />

      {/* Notice Scanner Dialog */}
      {showNoticeScannerDialog && scanningDocument && (
        <NoticeScannerDialog
          document={scanningDocument}
          caseId={caseId}
          onClose={() => {
            setShowNoticeScannerDialog(false);
            setScanningDocument(null);
          }}
          onEventCreated={() => {
            // Refresh the page to show new event
            fetchCase();
          }}
        />
      )}

      {/* Document Edit Dialog */}
      <DocumentEditDialog
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        document={editingDocument}
        caseId={caseId}
        folders={folders}
        onSave={() => {
          fetchDocuments(selectedFolder);
          setEditingDocument(null);
        }}
      />
    </div>
  );
}
